import { Button, Card } from '@repo/shadcn';
import { KeyboardEvent, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';

import {
    // DateInputField,
    DatePickerField,
    TextField,
} from '@components/form/fields';
import {
    AutoCompleteInput,
    Option,
} from '@components/ui/autocomplete/auto-complete-input';

import { UrlSearchFilterProperty } from '@/constants';
import { useGetProductsV2Query } from '@/features/slice/products/product/productApiSlice';
import { useUrlFilter } from '@/hooks';
import { TQueryFilterParams } from '@/types';

import { TCreatePrePurchaseForm } from './create-pre-purchase.schema';
import { usePrePurchaseItemsFieldArrayContext } from './items-field-array.context';

export const ProductSelector = () => {
    const quantityRef = useRef<HTMLInputElement>(null);

    const clearButtonRef = useRef<HTMLButtonElement>(null);

    const { debouncedSearchText, setSearchText, getSearchFilter } =
        useUrlFilter();

    const brandFilter = getSearchFilter(UrlSearchFilterProperty.BRAND_ID);

    const queryParams: TQueryFilterParams = {
        search: debouncedSearchText,
    };
    if (brandFilter) {
        queryParams[UrlSearchFilterProperty.BRAND_ID] = brandFilter;
    }

    const { data, isLoading, isFetching } = useGetProductsV2Query(queryParams);

    const { setValue, watch, getValues } =
        useFormContext<TCreatePrePurchaseForm>();

    const { prepend } = usePrePurchaseItemsFieldArrayContext();

    const currentSelectedItem = watch('current_selected_item');

    const clearCurrentSelectedItem = () => {
        setValue('current_selected_item.product_variation_id', '');
        setValue('current_selected_item.product_name', '');
        setValue('current_selected_item.quantity', undefined);
        setValue('current_selected_item.purchase_price', 0);
        setValue('current_selected_item.wholesale_price', 0);
        setValue('current_selected_item.retail_price', 0);
        setValue('current_selected_item.expiration_date', undefined);
    };

    const handleAddProduct = (quantityInputValue?: string) => {
        const selectedItem = getValues('current_selected_item');

        if (!selectedItem) {
            toast.error('No product selected to add.');
            return;
        }

        if (!selectedItem.product_name) {
            toast.error('Please select a product.');
            return;
        }

        const quantity =
            quantityInputValue === undefined
                ? Number(selectedItem.quantity)
                : Number(quantityInputValue);

        if (!Number.isFinite(quantity) || quantity <= 0) {
            toast.error('Please enter a valid quantity.');
            return;
        }

        if (
            selectedItem.purchase_price === undefined ||
            selectedItem.purchase_price < 0
        ) {
            toast.error('Please enter a valid purchase price.');
            return;
        }

        if (
            selectedItem.wholesale_price === undefined ||
            selectedItem.wholesale_price < 0
        ) {
            toast.error('Please enter a valid wholesale price.');
            return;
        }

        if (
            selectedItem.retail_price === undefined ||
            selectedItem.retail_price < 0
        ) {
            toast.error('Please enter a valid retail price.');
            return;
        }

        prepend({
            product_variation_id: selectedItem.product_variation_id,
            quantity,
            purchase_price: selectedItem.purchase_price,
            wholesale_price: selectedItem.wholesale_price,
            retail_price: selectedItem.retail_price,
            expiration_date: selectedItem.expiration_date,
            product_name: selectedItem.product_name,
        });

        clearCurrentSelectedItem();
        clearButtonRef.current?.click();
    };

    const handleQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
            return;
        }

        event.preventDefault();
        handleAddProduct(event.currentTarget.value);
    };

    const handleProductChange = (value: Option) => {
        // Handle product selection change

        if (!value || !value.value || !value.label) {
            clearCurrentSelectedItem();
            return;
        }

        setValue('current_selected_item.product_variation_id', value.value);
        setValue('current_selected_item.product_name', value.label);
        setValue('current_selected_item.quantity', undefined);
        setValue(
            'current_selected_item.purchase_price',
            (value.purchase_price as number) ?? 0,
        );
        setValue(
            'current_selected_item.wholesale_price',
            (value.wholesale_price as number) ?? 0,
        );
        setValue(
            'current_selected_item.retail_price',
            (value.retail_price as number) ?? 0,
        );
        setValue('current_selected_item.expiration_date', undefined);

        setTimeout(() => {
            quantityRef.current?.focus();
        }, 0);
    };

    const products =
        data?.data?.map((product) => ({
            label: `${product.full_product_name} (${product.unit_name})`,
            value: product.id,
            purchase_price: product.purchase_price,
            wholesale_price: product.wholesale_price,
            retail_price: product.retail_price,
        })) || [];

    const checkDisabled = (): boolean => {
        return !!(
            !currentSelectedItem ||
            !currentSelectedItem.product_variation_id ||
            // !currentSelectedItem.quantity ||

            (currentSelectedItem?.quantity &&
                currentSelectedItem.quantity <= 0) ||
            (currentSelectedItem?.purchase_price &&
                currentSelectedItem.purchase_price < 0) ||
            (currentSelectedItem?.wholesale_price &&
                currentSelectedItem.wholesale_price < 0) ||
            (currentSelectedItem?.retail_price &&
                currentSelectedItem.retail_price < 0)
        );
    };

    return (
        <Card className={'mb-[70px] space-y-2 p-4'}>
            <div>
                <h2>Product Name</h2>
                <AutoCompleteInput
                    placeholder={'Search product'}
                    options={products}
                    isLoading={isLoading || isFetching}
                    emptyMessage={'No products found'}
                    onValueChange={handleProductChange}
                    ref={clearButtonRef}
                    onInputChange={(value) => setSearchText(value)}
                />
            </div>

            <div className={'grid grid-cols-2 gap-1'}>
                <TextField<TCreatePrePurchaseForm>
                    name={'current_selected_item.quantity'}
                    label={'Quantity'}
                    type={'number'}
                    placeholder={'Enter quantity'}
                    ref={quantityRef}
                    inputProps={{
                        value: currentSelectedItem?.quantity ?? '',
                        onKeyDown: handleQuantityKeyDown,
                    }}
                />
                <div>
                    <DatePickerField<TCreatePrePurchaseForm>
                        name="current_selected_item.expiration_date"
                        label="Exp Date"
                    />
                </div>
            </div>

            <div className={'grid grid-cols-2 gap-1'}>
                <TextField<TCreatePrePurchaseForm>
                    name={'current_selected_item.purchase_price'}
                    label={'PP'}
                    placeholder={'Enter purchase price'}
                />

                <TextField<TCreatePrePurchaseForm>
                    name={'current_selected_item.wholesale_price'}
                    label={'WP'}
                    placeholder={'Enter wholesale price'}
                />

                <TextField<TCreatePrePurchaseForm>
                    name={'current_selected_item.retail_price'}
                    label={'MRP'}
                    placeholder={'Enter retail price'}
                />

                <Button
                    type={'button'}
                    onClick={() => handleAddProduct()}
                    className={'self-end'}
                    disabled={checkDisabled()}
                >
                    Add Product
                </Button>
            </div>
        </Card>
    );
};

ProductSelector.displayName = 'ProductSelector';
